import {
  Alert,
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Link,
  Select,
  Switch,
} from '@chakra-ui/react';
import { useCallback, useState } from 'react';
import { MdCloudUpload } from 'react-icons/md';
import { Link as RouterLink } from 'react-router-dom';

import { ImportCsvDelimiter, Task } from '@app/api/tasks';
import { Paper, Txt } from '@app/components/common';
import { Upload } from '@app/components/common/upload';
import { useAlert, useApi } from '@app/hooks';
import { Confirm } from '@app/pages/equipments/imports/components/confirm';

import { OutletRoutesImportScheme } from './outlet-routes-scheme';

const DELIM_OPTIONS: { id: ImportCsvDelimiter; name: string }[] = [
  { id: 'auto', name: 'Авто' },
  { id: 'semicolon', name: 'Точка с запятой (;)' },
  { id: 'comma', name: 'Запятая (,)' },
  { id: 'tab', name: 'Табуляция' },
];

const FILE_ACCEPT = [
  '.csv',
  '.xlsx',
  'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
].join(',');

export const OutletRoutesImport = () => {
  const api = useApi();
  const { error, success, warning } = useAlert();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File>();
  const [header, setHeader] = useState(true);
  const [dryRun, setDryRun] = useState(false);
  const [deactivateExisting, setDeactivateExisting] = useState(true);
  const [delim, setDelim] = useState<ImportCsvDelimiter>('auto');
  const [createdTask, setCreatedTask] = useState<Task | null>(null);

  const onFileChange = useCallback((_: string, nextFile: File) => {
    setFile(nextFile);
  }, []);

  const onImportClick = useCallback(() => {
    if (!file) {
      error('Файл не выбран');
      return;
    }
    setOpen(true);
  }, [error, file]);

  const onImport = useCallback(async () => {
    if (!file) {
      return;
    }

    setLoading(true);
    try {
      const response = await api.tasks.importOutletRoutes({
        file,
        dry_run: dryRun,
        deactivate_existing: deactivateExisting,
        header,
        delim,
      });

      if (response?.code !== 200 || !response.data) {
        return;
      }

      const task = response.data;
      setCreatedTask(task);
      const skipped = task.skipped ?? 0;
      if (skipped > 0) {
        warning(
          `Задача #${task.id} создана. Пропущено строк без ID точки или названия маршрута: ${skipped}` +
            (task.warnings?.length
              ? `. ${task.warnings.slice(0, 3).join('; ')}`
              : '')
        );
      } else {
        success(
          `Задача #${task.id} (Импорт маршрутов ТТ) поставлена в очередь`
        );
      }
    } catch (e) {
      console.error(e);
      error(
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Не удалось создать задачу'
      );
    } finally {
      setLoading(false);
    }
  }, [
    api.tasks,
    deactivateExisting,
    delim,
    dryRun,
    error,
    file,
    header,
    success,
    warning,
  ]);

  const onConfirm = useCallback(() => {
    setOpen(false);
    onImport();
  }, [onImport]);

  return (
    <Paper>
      <Flex direction="column" gap={6} p={{ base: 3, md: 5 }}>
        <Txt as="h1" fontSize="xl" fontWeight="bold">
          Импорт маршрутов
        </Txt>
        <Flex
          direction={{ base: 'column', xl: 'row' }}
          gap={6}
          alignItems="flex-start"
        >
          <Flex
            direction="column"
            gap={6}
            w={{ base: '100%', xl: '440px' }}
            flexShrink={0}
          >
            <Box>
              <Alert
                status="info"
                alignItems="flex-start"
                flexDirection="column"
              >
                <Txt>
                  Файл отправляется на шлюз методом POST /import/outlet_routes и
                  ставится в очередь как фоновая задача импорта маршрутов.
                  Каждая строка — одна привязка торговой точки к маршруту.
                </Txt>
                <Box as="ul" pl={5} mt={2}>
                  <Box as="li">1 — ID торговой точки</Box>
                  <Box as="li">2 — название маршрута, точное совпадение</Box>
                  <Box as="li">
                    3 — дни посещения или «не посещается». Пустая ячейка при
                    создании — без дней посещения
                  </Box>
                </Box>
              </Alert>
            </Box>

            <Box>
              <Txt fontWeight="semibold" mb={3}>
                Настройки запроса
              </Txt>
              <Flex direction="column" gap={4}>
                <FormControl display="flex" alignItems="flex-start" gap={3}>
                  <Switch
                    id="outlet-routes-header"
                    isChecked={header}
                    colorScheme="brand"
                    mt={1}
                    onChange={(event) => setHeader(event.target.checked)}
                  />
                  <Box>
                    <FormLabel htmlFor="outlet-routes-header" mb={0}>
                      Первая строка — заголовок
                    </FormLabel>
                    <FormHelperText mt={0}>
                      По умолчанию включено: первая строка файла не
                      импортируется.
                    </FormHelperText>
                  </Box>
                </FormControl>

                <FormControl display="flex" alignItems="flex-start" gap={3}>
                  <Switch
                    id="outlet-routes-dry-run"
                    isChecked={dryRun}
                    colorScheme="brand"
                    mt={1}
                    onChange={(event) => setDryRun(event.target.checked)}
                  />
                  <Box>
                    <FormLabel htmlFor="outlet-routes-dry-run" mb={0}>
                      Только отчёт, без записи
                    </FormLabel>
                    <FormHelperText mt={0}>
                      Показать действия по строкам и не менять данные в SNEG.
                    </FormHelperText>
                  </Box>
                </FormControl>

                <FormControl display="flex" alignItems="flex-start" gap={3}>
                  <Switch
                    id="outlet-routes-deactivate"
                    isChecked={deactivateExisting}
                    colorScheme="brand"
                    mt={1}
                    onChange={(event) =>
                      setDeactivateExisting(event.target.checked)
                    }
                  />
                  <Box>
                    <FormLabel htmlFor="outlet-routes-deactivate" mb={0}>
                      Деактивировать старые привязки
                    </FormLabel>
                    <FormHelperText mt={0}>
                      По умолчанию включено: перед новой привязкой
                      деактивируются все текущие активные связи точки.
                    </FormHelperText>
                  </Box>
                </FormControl>

                <FormControl>
                  <FormLabel>Разделитель CSV</FormLabel>
                  <Select
                    size="sm"
                    value={delim}
                    onChange={(event) =>
                      setDelim(event.target.value as ImportCsvDelimiter)
                    }
                  >
                    {DELIM_OPTIONS.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </Select>
                  <FormHelperText>
                    delim. Для CSV. «Авто» — разделитель определяется
                    автоматически.
                  </FormHelperText>
                </FormControl>
              </Flex>
            </Box>

            <Box minH="140px" display="flex" flexDirection="column">
              <Upload
                onChange={onFileChange}
                label="Выберите CSV или XLSX с маршрутами"
                accept={FILE_ACCEPT}
              />
            </Box>

            {createdTask && (
              <Alert status="success">
                <Txt>
                  Задача #{createdTask.id} создана.{' '}
                  <Link
                    as={RouterLink}
                    to={`/tools/tasks/${createdTask.id}`}
                    color="brand.600"
                    fontWeight="semibold"
                  >
                    Открыть в фоновых задачах
                  </Link>
                </Txt>
              </Alert>
            )}

            <Box>
              <Button
                isLoading={loading}
                onClick={onImportClick}
                variant="solid"
                colorScheme="brand"
                leftIcon={<MdCloudUpload size="24px" />}
              >
                Загрузить
              </Button>
            </Box>
          </Flex>
          <Box flex="1" minW={0} w="100%">
            <OutletRoutesImportScheme />
          </Box>
        </Flex>
      </Flex>
      <Confirm
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={onConfirm}
      />
    </Paper>
  );
};
